import { Container, Graphics, Text, type ColorSource } from "pixi.js";

export interface NameplateOptions {
  text: string;
  color?: ColorSource;
  background?: ColorSource;
  paddingX?: number;
  paddingY?: number;
  fontSize?: number;
  /** Vertical offset relative to entity origin (px, world-space). */
  yOffset?: number;
}

const DEFAULT_FONT = "ui-monospace, monospace";

/**
 * Lightweight nameplate as a Container holding a rounded background + Text.
 * Use as a sprite over an entity. The container origin is centered on x and
 * sits at -yOffset above origin.
 */
export class Nameplate extends Container {
  readonly bg: Graphics;
  readonly textNode: Text;
  private paddingX: number;
  private paddingY: number;

  constructor(opts: NameplateOptions) {
    super({ label: "nameplate" });
    this.paddingX = opts.paddingX ?? 4;
    this.paddingY = opts.paddingY ?? 1;
    this.bg = new Graphics();
    this.textNode = new Text({
      text: opts.text,
      style: {
        fontFamily: DEFAULT_FONT,
        fontSize: opts.fontSize ?? 11,
        fill: opts.color ?? 0xe6e8ee,
      },
    });
    this.textNode.anchor.set(0.5, 0.5);
    this.addChild(this.bg);
    this.addChild(this.textNode);
    this.position.set(0, opts.yOffset ?? -22);
    this.repaint(opts.background ?? 0x000000);
  }

  setText(value: string): void {
    if (this.textNode.text === value) return;
    this.textNode.text = value;
    this.repaint();
  }

  setColor(color: ColorSource): void {
    this.textNode.style.fill = color;
  }

  private repaint(bgColor?: ColorSource): void {
    const w = this.textNode.width + this.paddingX * 2;
    const h = this.textNode.height + this.paddingY * 2;
    this.bg.clear();
    this.bg.roundRect(-w / 2, -h / 2, w, h, 3).fill({ color: bgColor ?? 0x000000, alpha: 0.55 });
    this.bg.stroke({ color: 0xffffff, width: 1, alpha: 0.08 });
  }
}
